// Related Collections slider: progressive enhancement over the theme's native
// CSS scroll-snap slider (slider--tablet). Without JS, or before Swiper loads,
// the list still scrolls natively with the browser scrollbar hidden. Swiper
// adds mouse dragging, mouse wheel / trackpad scrolling and the optional
// branded scrollbar, and is only created while the slider breakpoint matches
// and the section is close to the viewport.
if (!customElements.get("related-collections-slider")) {
  const SLIDER_QUERY = "(max-width: 989px)";
  const SWIPE_NAV_ROOTS = [document.documentElement, document.body];

  class RelatedCollectionsSlider extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector(".collection-list");
      this.scrollbar = this.querySelector(".related-collections__scrollbar");
      if (!this.list) return;

      this.swiper = null;
      this.isNearViewport = false;
      this.onWheel = (event) => this.containHorizontalWheel(event);
      this.onPointerEnter = (event) => {
        if (event.pointerType === "mouse") this.toggleSwipeNavigation(false);
      };
      this.onPointerLeave = () => this.toggleSwipeNavigation(true);
      this.mediaQuery = window.matchMedia(SLIDER_QUERY);
      this.onMediaChange = () => this.update();
      this.mediaQuery.addEventListener("change", this.onMediaChange);

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.isNearViewport = true;
          this.observer.disconnect();
          this.update();
        },
        { rootMargin: "200px 0px" }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.mediaQuery?.removeEventListener("change", this.onMediaChange);
      this.destroySwiper();
    }

    update() {
      if (this.isNearViewport && this.mediaQuery.matches) {
        this.initSwiper();
      } else {
        this.destroySwiper();
      }
    }

    initSwiper() {
      if (this.swiper || typeof window.Swiper !== "function") return;

      const slides = [...this.list.children];
      if (!slides.length) return;

      // Swiper positions slides itself, so carry over the spacing the native
      // slider gets from CSS (grid gap and the page-gutter inset at both
      // ends) before the swiper-active class removes it.
      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      const inset = parseFloat(getComputedStyle(slides[0]).marginLeft) || 0;

      this.list.scrollLeft = 0;
      this.classList.add("swiper");
      this.list.classList.add("swiper-wrapper");
      slides.forEach((slide) => slide.classList.add("swiper-slide"));

      this.swiper = new window.Swiper(this, {
        slidesPerView: "auto",
        spaceBetween: gap,
        slidesOffsetBefore: inset,
        slidesOffsetAfter: inset,
        grabCursor: true,
        watchOverflow: true,
        // Move freely with momentum, like native scrolling, instead of
        // stepping slide by slide. The wheel/trackpad then scrolls by its
        // actual distance too.
        freeMode: {
          enabled: true,
          momentum: true,
          sticky: false,
        },
        // Only horizontal wheel/trackpad movement (or Shift + wheel) moves
        // the slides; vertical scrolling always goes straight to the page.
        mousewheel: {
          forceToAxis: true,
        },
        ...(this.scrollbar && {
          scrollbar: {
            el: this.scrollbar,
            draggable: true,
            hide: false,
          },
        }),
      });
      this.addEventListener("wheel", this.onWheel, { passive: false });
      this.addEventListener("pointerenter", this.onPointerEnter);
      this.addEventListener("pointerleave", this.onPointerLeave);
    }

    // Browsers decide on trackpad back/forward navigation at the start of the
    // gesture, so blocking wheel events alone misses it whenever the first
    // event isn't clearly horizontal (and Chrome makes the rest of the
    // gesture uncancelable). overscroll-behavior-x: none on the page root is
    // the supported way to turn swipe navigation off; it's only applied while
    // a mouse/trackpad pointer is over an active slider, so the gesture keeps
    // working everywhere else on the page.
    toggleSwipeNavigation(enabled) {
      if (!enabled && (!this.swiper || this.swiper.isLocked)) return;
      if (enabled === !this.swipeNavigationDisabled) return;
      this.swipeNavigationDisabled = !enabled;
      SWIPE_NAV_ROOTS.forEach((root) => {
        root.style.overscrollBehaviorX = enabled ? "" : "none";
      });
    }

    // At either end Swiper stops handling the wheel, and a horizontal
    // trackpad swipe that reaches the browser becomes back/forward
    // navigation. Keep horizontal gestures inside the slider; vertical ones
    // are left alone so the page always scrolls.
    containHorizontalWheel(event) {
      if (this.swiper?.isLocked) return;
      if (event.deltaX !== 0 && Math.abs(event.deltaX) >= Math.abs(event.deltaY)) {
        event.preventDefault();
      }
    }

    destroySwiper() {
      if (!this.swiper) return;
      this.removeEventListener("wheel", this.onWheel);
      this.removeEventListener("pointerenter", this.onPointerEnter);
      this.removeEventListener("pointerleave", this.onPointerLeave);
      this.toggleSwipeNavigation(true);
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.classList.remove("swiper");
      this.list.classList.remove("swiper-wrapper");
      [...this.list.children].forEach((slide) =>
        slide.classList.remove("swiper-slide")
      );
    }
  }

  customElements.define("related-collections-slider", RelatedCollectionsSlider);
}
